import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BENEFIT_TYPE_LABELS,
  COST_LABELS,
  calculateBCR,
  calculatePaybackMonths,
  calculateROI,
  calculateTotalBenefits,
  calculateTotalCosts,
  type ProjectROIData,
} from '@/data/roiModelData';
import { LIDER_IA_COMPONENTS, DIMENSION_NAMES } from '@/data/liderIAEvaluationData';
import type { ComponentScore } from '@/hooks/useProjectEvaluations';
import type { BuildBuyEvaluationRow } from '@/hooks/useBuildBuyEvaluation';

export interface ProjectPDFInput {
  project: any;
  participant: { full_name?: string | null; email?: string | null; company?: string | null; area?: string | null } | null;
  cohort: { name?: string | null } | null;
  tools: string[];
  evaluation: {
    total_score: number | null;
    classification: string | null;
    component_scores: ComponentScore[] | null;
    general_comments: string | null;
    suggestions: string[] | null;
  } | null;
  deliverables: Array<{
    name: string;
    description?: string | null;
    status: string;
    submitted_at?: string | null;
    feedback?: string | null;
    comment?: string | null;
    score?: number | null;
    tool_used?: string | null;
    file_url?: string | null;
    file_urls?: any;
  }>;
  buildBuy: BuildBuyEvaluationRow | null;
  suggestions: Array<{ suggested_content?: any; generated_at?: string | null; model?: string | null }>;
}

const PETROL: [number, number, number] = [13, 84, 99]; // teal/petrol brand
const MUTED: [number, number, number] = [100, 116, 139];
const TEXT: [number, number, number] = [30, 41, 59];
const ACCENT: [number, number, number] = [20, 184, 166];

const SOLUTION_TYPE_LABELS: Record<number, string> = {
  1: 'Creación de Contenido con IA',
  2: 'Vibecoding y Creación de Aplicaciones con IA',
  3: 'Asistente / Agente IA',
  4: 'Automatización IA',
  5: 'Análisis de Datos con IA',
};

const STATUS_LABELS: Record<string, string> = {
  ideation: 'Ideación',
  planning: 'Planificación',
  in_progress: 'En progreso',
  review: 'En revisión',
  completed: 'Completado',
  paused: 'Pausado',
  submitted: 'Entregado',
  validated: 'Validado',
  needs_adjustments: 'Ajustes requeridos',
  pending: 'Pendiente',
};

const BB_REC_LABELS: Record<string, string> = {
  buy_saas: 'Comprar SaaS',
  build_internal: 'Construir internamente',
  outsource_dev: 'Tercerizar desarrollo',
  hybrid: 'Híbrido',
};

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const slug = (s: string) =>
  (s || 'proyecto').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const W = 210;
const M = 14;

/**
 * Render a single project's content into an existing jsPDF document.
 * Starts on the current page; caller is responsible for adding a page break beforehand if needed.
 */
function renderProjectIntoDoc(doc: jsPDF, input: ProjectPDFInput): void {
  let y = 0;

  const ensureSpace = (needed: number) => {
    if (y + needed > 285) {
      doc.addPage();
      y = M;
    }
  };

  const sectionTitle = (label: string) => {
    ensureSpace(14);
    doc.setFillColor(...PETROL);
    doc.rect(M, y, W - 2 * M, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(label, M + 3, y + 5.6);
    y += 11;
    doc.setTextColor(...TEXT);
  };

  const para = (text: string, opts: { bold?: boolean; size?: number; color?: [number, number, number] } = {}) => {
    if (!text) return;
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setFontSize(opts.size ?? 10);
    doc.setTextColor(...(opts.color ?? TEXT));
    const lines = doc.splitTextToSize(text, W - 2 * M);
    for (const ln of lines) {
      ensureSpace(5);
      doc.text(ln, M, y);
      y += 5;
    }
  };

  const kv = (label: string, value: string) => {
    ensureSpace(5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...MUTED);
    doc.text(`${label}:`, M, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...TEXT);
    const labelW = doc.getTextWidth(`${label}:`) + 2;
    const lines = doc.splitTextToSize(value || '—', W - 2 * M - labelW);
    doc.text(lines[0] || '—', M + labelW, y);
    y += 5;
    for (let i = 1; i < lines.length; i++) {
      ensureSpace(5);
      doc.text(lines[i], M + labelW, y);
      y += 5;
    }
  };

  // ─── COVER / HEADER ─────────────────────────────────────────
  doc.setFillColor(...PETROL);
  doc.rect(0, 0, W, 38, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('LIDER IA · PORTAFOLIO DE PROYECTOS', M, 12);
  doc.setFontSize(18);
  const title = doc.splitTextToSize(input.project.name || 'Proyecto', W - 2 * M);
  doc.text(title.slice(0, 2), M, 22);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const stype = SOLUTION_TYPE_LABELS[input.project.solution_type] || 'Solución de IA';
  const status = STATUS_LABELS[input.project.status || 'ideation'] || input.project.status || 'Ideación';
  doc.text(`${stype}  ·  Estado: ${status}  ·  Creado: ${fmtDate(input.project.created_at)}`, M, 32);
  y = 46;
  doc.setTextColor(...TEXT);

  // Quick summary
  const score = input.evaluation?.total_score ?? 0;
  const classification = input.evaluation?.classification || (score >= 400 ? 'Quick Win' : score >= 300 ? 'Strategic Play' : score >= 200 ? 'Experiment' : 'Back-log');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(28);
  doc.setTextColor(...ACCENT);
  doc.text(`${score}`, M, y);
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text('/ 500 pts', M + doc.getTextWidth(`${score}`) + 2, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...TEXT);
  doc.text(`Clasificación: ${classification}`, M + 60, y - 4);
  y += 8;

  // ─── INFO ────────────────────────────────────────────────
  sectionTitle('Información general');
  kv('Participante', input.participant?.full_name || '—');
  kv('Email', input.participant?.email || '—');
  kv('Empresa', input.participant?.company || '—');
  kv('Área', input.participant?.area || '—');
  kv('Cohorte', input.cohort?.name || 'Sin cohorte');
  kv('Tipo de solución', stype);
  kv('Estado', status);
  kv('Visibilidad organización', input.project.is_org_visible ? 'Sí' : 'No');
  kv('Público', input.project.is_public ? 'Sí' : 'No');
  if (input.tools.length) kv('Herramientas', input.tools.join(', '));
  y += 2;

  // ─── DESCRIPTION ─────────────────────────────────────────
  sectionTitle('Problema y solución');
  para('Problema:', { bold: true, size: 10 });
  para(input.project.problem || '—');
  y += 2;
  para('Solución propuesta:', { bold: true, size: 10 });
  para(input.project.solution_description || '—');
  y += 2;
  if (input.project.target_audience) {
    para('Público objetivo:', { bold: true });
    para(input.project.target_audience);
    y += 2;
  }
  if (input.project.data_used) {
    para('Datos utilizados:', { bold: true });
    para(input.project.data_used);
    y += 2;
  }

  // KPI
  if (input.project.kpi_name || input.project.kpi_current_value || input.project.kpi_target_value) {
    sectionTitle('KPI principal');
    kv('Nombre del KPI', input.project.kpi_name || '—');
    kv('Valor actual', input.project.kpi_current_value || '—');
    kv('Valor objetivo', input.project.kpi_target_value || '—');
    kv('Frecuencia de medición', input.project.measurement_frequency || '—');
  }

  // ─── RUBRIC ANSWERS (preguntas del entregable / proyecto) ──
  const rubric = (input.project.rubric_answers || {}) as Record<string, string>;
  const rubricEntries = Object.entries(rubric).filter(([, v]) => (v || '').trim().length > 0);
  if (rubricEntries.length) {
    sectionTitle('Respuestas a preguntas LIDER-IA');
    for (const [cid, answer] of rubricEntries) {
      const comp = LIDER_IA_COMPONENTS.find((c) => c.id === cid);
      const qLabel = comp ? `${comp.dimensionLetter} · ${comp.name}` : cid;
      const question = comp?.question || '';
      ensureSpace(8);
      para(qLabel, { bold: true, size: 10, color: PETROL });
      if (question) para(question, { color: MUTED, size: 9 });
      para(answer);
      y += 2;
    }
  }

  // ─── DELIVERABLES ────────────────────────────────────────
  if (input.deliverables.length) {
    sectionTitle('Entregables');
    autoTable(doc, {
      startY: y,
      head: [['Nombre', 'Estado', 'Fecha', 'Score', 'Herramienta']],
      body: input.deliverables.map((d) => [
        d.name || '—',
        STATUS_LABELS[d.status] || d.status || '—',
        fmtDate(d.submitted_at),
        d.score != null ? String(d.score) : '—',
        d.tool_used || '—',
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: PETROL, textColor: 255 },
      margin: { left: M, right: M },
    });
    y = (doc as any).lastAutoTable.finalY + 4;

    for (const d of input.deliverables) {
      ensureSpace(14);
      para(d.name || 'Entregable', { bold: true, color: PETROL });
      if (d.description) para(d.description);
      if (d.comment) {
        para('Comentario del participante:', { bold: true, size: 9 });
        para(d.comment);
      }
      if (d.feedback) {
        para('Feedback del mentor:', { bold: true, size: 9 });
        para(d.feedback);
      }
      const files: string[] = Array.isArray(d.file_urls) ? d.file_urls : d.file_url ? [d.file_url] : [];
      if (files.length) {
        para('Archivos:', { bold: true, size: 9 });
        for (const f of files) para(`• ${typeof f === 'string' ? f : (f as any)?.url || JSON.stringify(f)}`, { size: 8, color: MUTED });
      }
      y += 2;
    }
  }

  // ─── EVALUATION ──────────────────────────────────────────
  if (input.evaluation) {
    doc.addPage();
    y = M;
    sectionTitle('Evaluación LIDER-IA');
    kv('Puntaje total', `${score} / 500`);
    kv('Clasificación', classification);
    if (input.evaluation.general_comments) {
      para('Comentarios generales:', { bold: true });
      para(input.evaluation.general_comments);
      y += 2;
    }

    const scores = input.evaluation.component_scores || [];
    if (scores.length) {
      autoTable(doc, {
        startY: y,
        head: [['Dim.', 'Componente', 'Peso %', 'Score (0-5)']],
        body: scores.map((s) => [
          `${s.dimension} · ${DIMENSION_NAMES[s.dimension] || ''}`,
          s.name,
          String(s.weight),
          String(s.score),
        ]),
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: PETROL, textColor: 255 },
        margin: { left: M, right: M },
      });
      y = (doc as any).lastAutoTable.finalY + 4;

      sectionTitle('Justificación por componente');
      for (const s of scores) {
        ensureSpace(10);
        para(`${s.dimension} · ${s.name}  —  ${s.score}/5  (peso ${s.weight}%)`, { bold: true, color: PETROL, size: 10 });
        para(s.feedback || '—');
        y += 1.5;
      }
    }

    if (input.evaluation.suggestions?.length) {
      sectionTitle('Sugerencias de la evaluación IA');
      for (const s of input.evaluation.suggestions) para(`• ${s}`);
    }
  }

  // ─── ROI ─────────────────────────────────────────────────
  const roi = input.project.roi_data as ProjectROIData | null;
  if (roi && roi.costs && roi.benefits) {
    doc.addPage();
    y = M;
    sectionTitle('Análisis ROI');
    const totalCosts = calculateTotalCosts(roi.costs);
    const totalBenefits = calculateTotalBenefits(roi.benefits);
    const roiPct = calculateROI(totalBenefits, totalCosts);
    const bcr = calculateBCR(totalBenefits, totalCosts);
    const payback = calculatePaybackMonths(totalBenefits, totalCosts);

    autoTable(doc, {
      startY: y,
      head: [['Indicador', 'Valor']],
      body: [
        ['Costos totales anuales', fmtCurrency(totalCosts)],
        ['Beneficios totales anuales', fmtCurrency(totalBenefits)],
        ['ROI', `${roiPct.toFixed(1)} %`],
        ['BCR (Beneficio/Costo)', bcr.toFixed(2)],
        ['Payback', Number.isFinite(payback) ? `${payback.toFixed(1)} meses` : 'N/A'],
      ],
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: PETROL, textColor: 255 },
      margin: { left: M, right: M },
    });
    y = (doc as any).lastAutoTable.finalY + 4;

    sectionTitle('Detalle de costos');
    autoTable(doc, {
      startY: y,
      head: [['Concepto', 'Monto anual']],
      body: (Object.keys(roi.costs) as Array<keyof typeof roi.costs>).map((k) => [
        COST_LABELS[k]?.name || k,
        fmtCurrency(roi.costs[k]),
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: PETROL, textColor: 255 },
      margin: { left: M, right: M },
    });
    y = (doc as any).lastAutoTable.finalY + 4;

    sectionTitle('Detalle de beneficios');
    autoTable(doc, {
      startY: y,
      head: [['Tipo', 'Descripción', 'Monto anual']],
      body: roi.benefits.map((b) => [
        BENEFIT_TYPE_LABELS[b.type] || b.type,
        b.description || '—',
        b.type === 'not_defined' ? 'Intangible' : fmtCurrency(b.amount),
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: PETROL, textColor: 255 },
      margin: { left: M, right: M },
    });
    y = (doc as any).lastAutoTable.finalY + 4;
  }

  // ─── BUILD vs BUY ────────────────────────────────────────
  if (input.buildBuy) {
    ensureSpace(40);
    sectionTitle('Evaluación Build / Buy / Outsource');
    const r = input.buildBuy.result;
    kv('Recomendación', BB_REC_LABELS[r.recommendation] || r.recommendation);
    kv('Confianza', `${Math.round((r.confidence || 0) * 100)} %`);
    if (r.rationale) {
      para('Justificación:', { bold: true });
      para(r.rationale);
      y += 2;
    }
    if (r.alignment) {
      para('Alineación estratégica:', { bold: true });
      para(r.alignment);
      y += 2;
    }
    if (r.risks?.length) {
      para('Riesgos:', { bold: true });
      r.risks.forEach((x) => para(`• ${x}`));
      y += 2;
    }
    if (r.conditions?.length) {
      para('Condiciones:', { bold: true });
      r.conditions.forEach((x) => para(`• ${x}`));
      y += 2;
    }
    if (r.next_steps?.length) {
      para('Próximos pasos:', { bold: true });
      r.next_steps.forEach((x) => para(`• ${x}`));
      y += 2;
    }
  }

  // ─── SUGGESTIONS ─────────────────────────────────────────
  if (input.suggestions?.length) {
    sectionTitle('Sugerencias y auditoría IA');
    for (const s of input.suggestions) {
      ensureSpace(10);
      const header = [s.model || 'IA', s.generated_at ? fmtDate(s.generated_at) : null].filter(Boolean).join(' · ');
      if (header) para(header, { bold: true, color: PETROL, size: 10 });
      const content = s.suggested_content;
      const text = typeof content === 'string' ? content : content ? JSON.stringify(content, null, 2) : '—';
      para(text);
      y += 2;
    }
  }
}

function applyFooter(doc: jsPDF, label: string) {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(label, M, 292);
    doc.text(`Pág. ${i} / ${pageCount}`, W - M, 292, { align: 'right' });
  }
}

export async function generateProjectPDF(input: ProjectPDFInput): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  renderProjectIntoDoc(doc, input);
  applyFooter(
    doc,
    `LIDER IA · Reporte de proyecto · Generado ${new Date().toLocaleDateString('es-ES')} · ID ${input.project.id?.slice(0, 8) || ''}`,
  );
  const filename = `proyecto-${slug(input.project.name)}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

/**
 * Build a single PDF containing all selected projects (one after another, separated by page breaks),
 * with a cover/index page listing every included project.
 */
export async function generateMultiProjectPDF(inputs: ProjectPDFInput[], fileName?: string): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // ── Cover page ─────────────────────────────────────────────
  doc.setFillColor(...PETROL);
  doc.rect(0, 0, W, 60, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('LIDER IA · PORTAFOLIO DE PROYECTOS', M, 16);
  doc.setFontSize(24);
  doc.text('Reporte consolidado del portafolio', M, 32);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(
    `${inputs.length} proyecto${inputs.length === 1 ? '' : 's'}  ·  Generado ${new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}`,
    M,
    44,
  );

  // Index table
  autoTable(doc, {
    startY: 70,
    head: [['#', 'Proyecto', 'Empresa', 'Cohorte', 'Score']],
    body: inputs.map((it, i) => [
      String(i + 1),
      it.project.name || '—',
      it.participant?.company || '—',
      it.cohort?.name || '—',
      String(it.evaluation?.total_score ?? 0),
    ]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: PETROL, textColor: 255 },
    margin: { left: M, right: M },
    columnStyles: { 0: { cellWidth: 10 }, 4: { halign: 'right', cellWidth: 18 } },
  });

  // ── One project per section, each starts on a new page ──────
  for (const input of inputs) {
    doc.addPage();
    renderProjectIntoDoc(doc, input);
  }

  applyFooter(
    doc,
    `LIDER IA · Reporte consolidado de portafolio · Generado ${new Date().toLocaleDateString('es-ES')} · ${inputs.length} proyectos`,
  );

  const name = fileName || `portafolio-proyectos-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(name);
}
